import { useState } from "react";

import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { CopyHash } from "../../features/web3/forex/components/CopyHash";

import { formatDateTime, formatInteger } from "../../lib/format";
import { FilterBar, FilterField, PAGE_SIZE_OPTIONS, PagePager, orDash } from "../forex/parts";
import { DIRECTIONS, WALLET_OPTIONS, useAdjustments } from "./adjustmentsApi";

const EMPTY = {
  username: "",
  wallet_type: "",
  admin: "",
  startDate: "",
  endDate: "",
  order: "desc",
  pageSize: "50",
};

const ORDER_OPTIONS = [
  { value: "desc", label: "Newest first" },
  { value: "asc", label: "Oldest first" },
];

const COLUMNS = [
  { label: "Date" },
  { label: "Admin" },
  { label: "User" },
  { label: "Wallet" },
  { label: "Direction" },
  { label: "Amount", align: "right" },
  { label: "Before", align: "right" },
  { label: "After", align: "right" },
  { label: "Note" },
  { label: "Tranx ID" },
];

// Totals cover the whole filtered selection, not the page, so they do not
// move while paging.
function TotalCard({ label, total, tone }) {
  return (
    <div className="space-y-1.5 rounded-lg border border-border/70 bg-white/[0.02] p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </div>
      <div className={`font-mono text-xl ${tone}`}>{total ? total.amount : "—"}</div>
      <div className="flex justify-between gap-3 text-xs">
        <span className="text-muted-foreground">Adjustments</span>
        <span className="font-mono text-foreground">{total ? formatInteger(total.count) : "—"}</span>
      </div>
    </div>
  );
}

export default function BalanceAdjustments() {
  const [direction, setDirection] = useState("");
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);

  const q = useAdjustments({
    ...applied,
    username: applied.username.trim(),
    admin: applied.admin.trim(),
    direction,
    page,
  });
  const data = q.data;

  return (
    <QueueLayout
      eyebrow="Finance"
      title="Balance"
      accent="adjustments"
      description="Every balance an admin moved by hand. Rows are permanent: a mistake is fixed by a second adjustment, and both stay here."
      statusOptions={DIRECTIONS}
      status={direction}
      onStatus={(d) => {
        setDirection(d);
        setPage(1);
      }}
      isFetching={q.isFetching}
      onRefresh={() => q.refetch()}
    >
      <FilterBar
        onApply={() => {
          setApplied(draft);
          setPage(1);
        }}
        onReset={() => {
          setDraft(EMPTY);
          setApplied(EMPTY);
          setPage(1);
        }}
      >
        <FilterField label="Username" name="username" draft={draft} setDraft={setDraft} placeholder="Exact username" />
        <FilterField label="Wallet" name="wallet_type" options={WALLET_OPTIONS} draft={draft} setDraft={setDraft} />
        <FilterField label="Admin email" name="admin" draft={draft} setDraft={setDraft} placeholder="All admins" />
        <FilterField label="From date" name="startDate" type="date" draft={draft} setDraft={setDraft} />
        <FilterField label="To date" name="endDate" type="date" draft={draft} setDraft={setDraft} />
        <FilterField label="Order" name="order" options={ORDER_OPTIONS} draft={draft} setDraft={setDraft} />
        <FilterField label="Page size" name="pageSize" options={PAGE_SIZE_OPTIONS} draft={draft} setDraft={setDraft} />
      </FilterBar>

      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <TotalCard label="Credited" total={data?.totals?.credited} tone="text-emerald-400" />
              <TotalCard label="Debited" total={data?.totals?.debited} tone="text-red-400" />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Totals cover the whole filtered selection, not this page.
            </p>
          </div>

          <QueueTable
            columns={COLUMNS}
            isLoading={q.isLoading}
            rows={data?.data}
            renderRow={(row) => {
              const credit = row.direction === "credit";
              return (
                <tr key={row._id} className="hover:bg-white/[0.02]">
                  <Td className="whitespace-nowrap font-mono text-muted-foreground">
                    {formatDateTime(row.createdAt)}
                  </Td>
                  <Td className="text-xs">{orDash(row.admin)}</Td>
                  <Td className="font-mono">{row.username}</Td>
                  <Td className="text-xs">{row.wallet_type}</Td>
                  <Td className={`text-xs font-medium ${credit ? "text-emerald-400" : "text-red-400"}`}>
                    {credit ? "Credit" : "Debit"}
                  </Td>
                  {/* Already truncated by the backend; shown as-is, never re-rounded. */}
                  <Td align="right" className={`font-mono ${credit ? "text-emerald-400" : "text-red-400"}`}>
                    {credit ? "+" : "−"}
                    {row.amount}
                  </Td>
                  <Td align="right" className="font-mono text-muted-foreground">{row.balanceBefore}</Td>
                  <Td align="right" className={`font-mono ${row.balanceAfter < 0 ? "text-red-400" : ""}`}>
                    {row.balanceAfter}
                  </Td>
                  {/* The note is the only place the reason exists: a missing one is worth seeing. */}
                  <Td className="max-w-xs text-xs">
                    {row.note ? row.note : <span className="text-amber-400">No reason given</span>}
                  </Td>
                  <Td>
                    <CopyHash value={row.tranx_id} head={10} tail={6} />
                  </Td>
                </tr>
              );
            }}
          />
          <PagePager data={data} onChange={setPage} label="adjustments" />
        </>
      )}
    </QueueLayout>
  );
}
